<template>
  <div
    class="suus-thumbnail-container"
    style="border-radius: 4px"
    :style="thumbnailStyles"
    @mouseenter="$emit('mouseenter')"
    @click="$emit('click')"
    ref="thumbnailContainer"
  >
    <div class="top-row">
      <gallery-thumbnail-action-button
        v-if="topLeftAction && showActions"
        :icon="topLeftAction.icon"
        @action="topLeftAction.action"
        :show-always="topLeftAction.showAlways"
        :tooltip="topLeftAction.tooltip"
      />
      <gallery-thumbnail-action-button
        v-if="topRightAction && showActions"
        :icon="topRightAction.icon"
        @action="topRightAction.action"
        :show-always="topRightAction.showAlways"
        :tooltip="topRightAction.tooltip"
      />
    </div>

    <div class="error-overlay-container" v-if="!image && !loading && !error">
      <div style="width: 100%; padding: 8px; overflow: hidden">
        <TextBody
          :font-size="0.9"
          style="
            overflow: hidden;
            word-wrap: break-word;
            text-overflow: ellipsis;
          "
        >
          Vorschau nicht verfügbar für
        </TextBody>
        <TextBody
          :font-size="0.9"
          v-if="alt"
          style="overflow: hidden; text-overflow: ellipsis"
        >
          {{ alt }}
        </TextBody>
      </div>
    </div>

    <div class="bottom-row">
      <gallery-thumbnail-action-bar
        v-if="actionBar && actionBar.length > 0 && showActions"
        :actions="actionBar"
      />
      <gallery-thumbnail-action-button
        v-if="bottomLeftAction && showActions"
        :icon="bottomLeftAction.icon"
        @action="bottomLeftAction.action"
        :class="bottomLeftAction.class"
        :show-always="bottomLeftAction.showAlways"
        :tooltip="bottomLeftAction.tooltip"
      />
    </div>
    <gallery-thumbnail-error-overlay
      v-if="error"
      :message="error.message"
      :action="error.action"
    />
    <gallery-thumbnail-loading-overlay v-if="loading && !error" />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from "vue"
import GalleryThumbnailActionButton from "./GalleryThumbnailActionButton.vue"
import GalleryThumbnailActionBar from "features/gallery/thumbnail/GalleryThumbnailActionBar.vue"
import GalleryThumbnailOverlay from "features/gallery/thumbnail/GalleryThumbnailOverlay.vue"
import GalleryThumbnailErrorOverlay from "features/gallery/thumbnail/GalleryThumbnailErrorOverlay.vue"
import GalleryThumbnailLoadingOverlay from "features/gallery/thumbnail/GalleryThumbnailLoadingOverlay.vue"
import TextBody from "components/UI/TextBody.vue"

export type GalleryThumbnailAction = {
  action: () => void
  icon: string
  class?: string
  showAlways?: boolean
  tooltip?: string
}

export type GalleryThumbnailError = {
  message: string
  action?: GalleryThumbnailAction
}

export default defineComponent({
  name: "GalleryThumbnail",
  components: {
    TextBody,
    GalleryThumbnailLoadingOverlay,
    GalleryThumbnailErrorOverlay,
    GalleryThumbnailOverlay,
    GalleryThumbnailActionBar,
    GalleryThumbnailActionButton,
  },
  props: {
    image: {
      type: String,
    },
    alt: {
      type: String,
      default: "",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Object as PropType<GalleryThumbnailError>,
    },
    actionBar: {
      type: Array as PropType<GalleryThumbnailAction[]>,
      default: () => [],
    },
    topLeftAction: {
      type: Object as PropType<GalleryThumbnailAction>,
    },
    topRightAction: {
      type: Object as PropType<GalleryThumbnailAction>,
    },
    bottomLeftAction: {
      type: Object as PropType<GalleryThumbnailAction>,
    },
    size: {
      type: Number,
      default: 128,
    },
  },
  computed: {
    thumbnailStyles(): Partial<CSSStyleDeclaration> {
      const styles: Partial<CSSStyleDeclaration> = {
        backgroundImage: this.image
          ? `url("${encodeURI(this.image)}")`
          : "none",
        backgroundColor: "#f2f2f2",
        aspectRatio: "1 / 1",
        backgroundSize: "cover",
      }
      if (this.size > 0) {
        styles.width = this.size - 8 + "px"
        styles.height = this.size - 8 + "px"
      }
      return styles
    },
  },
  setup(props, context) {
    if (
      props.bottomLeftAction &&
      props.actionBar &&
      props.actionBar.length > 0
    ) {
      throw new Error("Cannot have both bottomLeftAction and actionBar")
    }

    const thumbnailContainer = ref<HTMLDivElement | null>(null)

    const showActions = computed(() => {
      return !props.loading && !props.error
    })

    return {
      showActions,
      thumbnailContainer,
    }
  },
})
</script>

<style scoped>
.suus-thumbnail-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  box-sizing: content-box;
}

.top-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.bottom-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
}
</style>

<style>
.suus-thumbnail-container:hover .action-container {
  opacity: 1;
}

.suus-thumbnail-container.gallery-drag-chosen {
  filter: brightness(50%);
  transform: translate(0, -4px);
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2);
}

.error-overlay-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
  top: 0;
  bottom: 0;
}
</style>
